import { useEffect, useState } from "react";
import { Settings as SettingsIcon, Mic, Volume2, RefreshCw, KeyRound, LayoutGrid } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { IDS } from "../constants/testIds";
import { TIMEZONES, RETENTION_OPTIONS } from "../lib/time";
import { changePassword, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Field } from "./settings/Field";
import { ZelloTab } from "./settings/ZelloTab";
import { UsersTab } from "./settings/UsersTab";

const SYSTEM = "__system__";
function DeviceSelect({ testId, value, onChange, devices, kind, icon: Icon }) {
  const seen = new Set();
  const list = devices.filter(d => d.deviceId && !seen.has(d.deviceId) && seen.add(d.deviceId));
  const current = list.some(d => d.deviceId === value) ? value : SYSTEM;
  return (
    <Select value={current} onValueChange={v => onChange(v === SYSTEM ? "" : v)}>
      <SelectTrigger data-testid={testId}><Icon className="h-3.5 w-3.5 mr-2 text-slate-500" /><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={SYSTEM}>System default {kind}</SelectItem>
        {list.map((d, i) => <SelectItem key={d.deviceId} value={d.deviceId}>{d.label || `${kind} ${i + 1}`}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function Toggle({ testId, title, hint, checked, onChange, disabled }) {
  return (
    <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
      <div><div className="text-sm font-medium">{title}</div><div className="text-[11px] text-slate-500">{hint}</div></div>
      <Switch data-testid={testId} checked={!!checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}

export default function SettingsDialog({ settings, onSave, onResetLayout, speechSupported }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [devices, setDevices] = useState({ mics: [], speakers: [] });
  const [devError, setDevError] = useState("");
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  useEffect(() => { if (open) setForm(settings); }, [open, settings]);
  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));
  const onInput = (k) => (e) => set(k)(e.target.value);

  const loadDevices = async (requestPermission = false) => {
    try {
      if (requestPermission) { const s = await navigator.mediaDevices.getUserMedia({ audio: true }); s.getTracks().forEach(t => t.stop()); }
      const list = await navigator.mediaDevices.enumerateDevices();
      setDevices({ mics: list.filter(d => d.kind === "audioinput"), speakers: list.filter(d => d.kind === "audiooutput") });
      setDevError("");
    } catch (e) { setDevError(e.message || "Unable to list audio devices"); }
  };
  useEffect(() => { if (open) loadDevices(false); }, [open]);

  const save = async () => {
    setSaving(true);
    try { await onSave(form); setOpen(false); } finally { setSaving(false); }
  };
  const submitPw = async () => {
    try { await changePassword(pw); setPw({ current_password: "", new_password: "" }); toast.success("Password updated"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (!form) return null;
  const speakerSupported = typeof HTMLMediaElement !== "undefined" && "setSinkId" in HTMLMediaElement.prototype;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={IDS.header.settings} variant="outline" size="sm" className="gap-1.5" aria-label="Settings">
          <SettingsIcon className="h-4 w-4" /> <span className="hidden sm:inline">Settings</span>
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={IDS.settings.dialog} className="sm:max-w-xl max-h-[90vh] overflow-y-auto thin-scroll">
        <DialogHeader>
          <DialogTitle>Console settings</DialogTitle>
          <DialogDescription>Your preferences are saved to your account{isAdmin ? "; system settings apply to every dispatcher." : "."}</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="zello">
          <TabsList className={`grid ${isAdmin ? "grid-cols-6" : "grid-cols-5"}`}>
            <TabsTrigger value="zello" data-testid={IDS.zello.tab}>Zello</TabsTrigger>
            <TabsTrigger value="audio" data-testid="settings-tab-audio">Audio</TabsTrigger>
            <TabsTrigger value="general" data-testid="settings-tab-general">General</TabsTrigger>
            <TabsTrigger value="map" data-testid="settings-tab-map">Map</TabsTrigger>
            {isAdmin && <TabsTrigger value="users" data-testid={IDS.users.tab}>Users</TabsTrigger>}
            <TabsTrigger value="account" data-testid="settings-tab-account">Account</TabsTrigger>
          </TabsList>

          <TabsContent value="zello">
            <ZelloTab form={form} set={set} onInput={onInput} isAdmin={isAdmin} onSaveFirst={() => onSave(form)} />
          </TabsContent>

          <TabsContent value="audio" className="grid gap-3 pt-2">
            <Field label="Microphone (PTT input)">
              <DeviceSelect testId={IDS.settings.micDevice} value={form.mic_device_id} onChange={set("mic_device_id")} devices={devices.mics} kind="microphone" icon={Mic} />
            </Field>
            <Field label="Speaker (playback output)">
              <DeviceSelect testId={IDS.settings.speakerDevice} value={form.speaker_device_id} onChange={set("speaker_device_id")} devices={devices.speakers} kind="speaker" icon={Volume2} />
            </Field>
            {!speakerSupported && <p className="text-[11px] text-slate-500">This browser does not support choosing a speaker; playback uses the system default.</p>}
            {devError && <p className="text-[11px] text-red-600">{devError}</p>}
            <Button data-testid={IDS.settings.refreshDevices} variant="outline" size="sm" className="gap-1.5 w-fit" onClick={() => loadDevices(true)}>
              <RefreshCw className="h-3.5 w-3.5" /> Detect devices
            </Button>
            <Field label="Voice transcripts" hint={
              form.transcription === "whisper" ? "Paid: OpenAI Whisper (whisper-1) via your Universal Key, ≈ $0.006 per minute of audio. Transcribes every archived recording." :
              form.transcription === "browser" ? `Free: browser speech recognition while you hold PTT (Chrome/Edge only).${speechSupported ? "" : " Not supported in this browser."}` :
              "Transcripts are off. Bot chatter still shows its text."}>
              <Select value={form.transcription || "off"} onValueChange={set("transcription")} disabled={!isAdmin}>
                <SelectTrigger data-testid={IDS.settings.transcription}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off</SelectItem>
                  <SelectItem value="browser">Browser speech recognition (free)</SelectItem>
                  <SelectItem value="whisper">OpenAI Whisper — accurate, paid per minute</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </TabsContent>

          <TabsContent value="general" className="grid gap-3 pt-2">
            <Field label="Dispatcher display name">
              <Input data-testid={IDS.settings.dispatcherName} value={form.dispatcher_name || ""} onChange={onInput("dispatcher_name")} />
            </Field>
            <Field label="Time zone (clock & logs)">
              <Select value={form.timezone || "UTC"} onValueChange={set("timezone")}>
                <SelectTrigger data-testid={IDS.settings.timezone}><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{TIMEZONES.map(tz => <SelectItem key={tz} value={tz}>{tz.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Appearance">
              <Select value={form.theme || "light"} onValueChange={set("theme")}>
                <SelectTrigger data-testid={IDS.settings.theme}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="light">Light</SelectItem><SelectItem value="dark">Dark</SelectItem><SelectItem value="system">Follow system</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Audio logger retention" hint={isAdmin ? "System-wide. Older archive entries are purged automatically." : "Set by an administrator."}>
              <Select value={String(form.archive_retention_minutes ?? 0)} onValueChange={v => set("archive_retention_minutes")(Number(v))} disabled={!isAdmin}>
                <SelectTrigger data-testid={IDS.settings.retention}><SelectValue /></SelectTrigger>
                <SelectContent>{RETENTION_OPTIONS.map(o => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <div><div className="text-sm font-medium">Dashboard layout</div><div className="text-[11px] text-slate-500">Drag pane headers to rearrange; drag corners to resize. Saved to your account.</div></div>
              <Button data-testid={IDS.layout.reset} size="sm" variant="outline" className="gap-1.5" onClick={() => { onResetLayout(); setOpen(false); }}><LayoutGrid className="h-3.5 w-3.5" /> Reset</Button>
            </div>
          </TabsContent>

          <TabsContent value="map" className="grid gap-3 pt-2">
            <Field label="Map provider" hint={isAdmin ? undefined : "Set by an administrator."}>
              <Select value={form.map_provider} onValueChange={set("map_provider")} disabled={!isAdmin}>
                <SelectTrigger data-testid={IDS.settings.mapProvider}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="osm">OpenStreetMap (free, no key)</SelectItem><SelectItem value="google">Google Maps (requires API key)</SelectItem></SelectContent>
              </Select>
            </Field>
            {form.map_provider === "google" && isAdmin && (
              <Field label="Google Maps JavaScript API key">
                <Input data-testid={IDS.settings.googleKey} value={form.google_maps_key || ""} onChange={onInput("google_maps_key")} placeholder="AIza…" />
              </Field>
            )}
            <Toggle testId={IDS.settings.showTrails} title="Show unit trails" hint="Draw each unit's recent path when zoomed in (zoom 14+)." checked={form.show_trails} onChange={set("show_trails")} />
            <Toggle testId={IDS.settings.unitMovement} title="Simulate unit movement" hint="Nudges online units every few seconds so the map feels live." checked={form.unit_movement} onChange={set("unit_movement")} disabled={!isAdmin} />
            <Toggle testId={IDS.settings.sosAudible} title="Audible SOS alert" hint="Play a siren tone when a new SOS / emergency alert arrives." checked={form.sos_audible ?? true} onChange={set("sos_audible")} />
            <Field label="SOS simulation rate" hint={isAdmin ? "How often the simulator fires random emergency alerts." : "Set by an administrator."}>
              <Select value={String(form.sos_sim_rate ?? 0.01)} onValueChange={v => set("sos_sim_rate")(Number(v))} disabled={!isAdmin}>
                <SelectTrigger data-testid={IDS.settings.sosSimRate}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Off</SelectItem>
                  <SelectItem value="0.01">Rare (default)</SelectItem>
                  <SelectItem value="0.04">Occasional</SelectItem>
                  <SelectItem value="0.1">Frequent</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </TabsContent>

          {isAdmin && <TabsContent value="users"><UsersTab /></TabsContent>}

          <TabsContent value="account" className="grid gap-3 pt-2">
            <div className="rounded-md border border-slate-200 px-3 py-2 text-sm">
              <div className="font-medium">{user?.name || user?.email}</div>
              <div className="text-[11px] text-slate-500">{user?.email} · {user?.role}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid={IDS.settings.currentPassword} type="password" placeholder="Current password" value={pw.current_password} onChange={e => setPw(p => ({ ...p, current_password: e.target.value }))} />
              <Input data-testid={IDS.settings.newPassword} type="password" placeholder="New password (6+)" value={pw.new_password} onChange={e => setPw(p => ({ ...p, new_password: e.target.value }))} />
            </div>
            <Button data-testid={IDS.settings.changePassword} size="sm" variant="outline" className="gap-1.5 w-fit" onClick={submitPw} disabled={!pw.current_password || pw.new_password.length < 6}>
              <KeyRound className="h-3.5 w-3.5" /> Change password
            </Button>
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button data-testid={IDS.settings.save} onClick={save} disabled={saving}>{saving ? "Saving…" : "Save settings"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
